// SPDX-License-Identifier: AGPL-3.0-or-later
import DOMPurify from 'dompurify';

export const MAX_MERMAID_SOURCE_LENGTH = 20_000;
let nextId = 0;
let queue: Promise<unknown> = Promise.resolve();

// Mermaid's own frontMatterRegex: it only reads front matter at the very start.
const FRONT_MATTER = /^([^\S\n\r]*)-{3}\s*[\n\r](.*?)[\n\r]\1-{3}\s*[\n\r]+/s;

// Dark-surface steps of one categorical order (adjacent pairs checked for colour-blind separation),
// with the label ink that reads best on each. Mermaid's dark defaults are near-black.
const DARK_SCALE = [
	'#3987e5',
	'#d95926',
	'#199e70',
	'#c98500',
	'#d55181',
	'#008300',
	'#9085e9',
	'#e66767',
	'#2a9bb0',
	'#b8782e',
	'#b06cc4',
	'#7f9a1c',
];
const DARK_INK = '#141416';
const darkLabel = (color: string) => (color === '#008300' ? '#ffffff' : DARK_INK);
const DARK_THEME_VARIABLES: Record<string, string> = {
	pieOpacity: '1',
	pieSectionTextColor: DARK_INK,
	pieStrokeColor: '#1b1b1e',
	pieOuterStrokeColor: '#1b1b1e',
};
DARK_SCALE.forEach((color, index) => {
	DARK_THEME_VARIABLES[`cScale${index}`] = color;
	DARK_THEME_VARIABLES[`cScaleLabel${index}`] = darkLabel(color);
	DARK_THEME_VARIABLES[`pie${index + 1}`] = color;
	if (index < 8) {
		DARK_THEME_VARIABLES[`git${index}`] = color;
		DARK_THEME_VARIABLES[`gitBranchLabel${index}`] = darkLabel(color);
	}
});

// With SVG labels, Mermaid's circle, square, rounded and hexagon mindmap shapes leave the label
// group at x=0 without middle anchoring, so the text starts at the node's centre and is cut off.
const THEME_CSS = '.mindmap-node > g.label[transform^="translate(0,"] text { text-anchor: middle; }';

export function canRenderMermaid(source: string): boolean {
	// Source-controlled configuration can inject CSS or change rendering settings.
	// Keep those blocks as copyable source instead of applying overrides to Konvo.
	if (source.length > MAX_MERMAID_SOURCE_LENGTH || /%%\s*\{/.test(source)) return false;
	// A title is the only front matter allowed; config, theme and the rest stay as source.
	const frontMatter = FRONT_MATTER.exec(source.replace(/\r\n?/g, '\n'));
	return (
		!frontMatter || frontMatter[2].split('\n').every((line) => !line.trim() || /^title\s*:/.test(line.trimStart()))
	);
}

/**
 * Renders one diagram at a time. `isStale` is checked before a queued render starts, so sources
 * superseded while waiting (a streaming reply) are skipped and resolve to null.
 */
export function renderMermaid(source: string, dark: boolean, isStale?: () => boolean): Promise<string | null> {
	const render = queue
		.catch(() => {})
		.then(async () => {
			if (isStale?.()) return null;
			if (!canRenderMermaid(source)) throw new Error('Unsupported Mermaid source');
			const {default: mermaid} = await import('mermaid');
			if (isStale?.()) return null;
			mermaid.initialize({
				startOnLoad: false,
				securityLevel: 'strict',
				suppressErrorRendering: true,
				theme: dark ? 'dark' : 'default',
				themeVariables: dark ? DARK_THEME_VARIABLES : {},
				themeCSS: THEME_CSS,
				htmlLabels: false,
				flowchart: {htmlLabels: false},
				// Gantt lays out at the page width, then scales into the chat column; draw it at column width,
				// with short axis dates (a source axisFormat still wins) so the ticks don't run together.
				gantt: {useWidth: 640, fontSize: 12, sectionFontSize: 12, axisFormat: '%e %b'},
				fontFamily: 'system-ui, sans-serif',
				maxTextSize: MAX_MERMAID_SOURCE_LENGTH,
				maxEdges: 500,
			});
			const {svg} = await mermaid.render(`konvo-mermaid-${++nextId}`, source);
			return DOMPurify.sanitize(svg, {
				USE_PROFILES: {svg: true, svgFilters: true},
				FORBID_TAGS: ['foreignObject', 'a', 'image'],
				FORBID_ATTR: ['href', 'xlink:href'],
			});
		});
	queue = render;
	return render;
}
